
import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { 
  LineChart as RechartsLineChart, 
  Line, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip as RechartsTooltip, 
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell
} from 'recharts';
import { 
  DollarSign, 
  TrendingUp, 
  LineChart, 
  ArrowUpRight,
  ArrowDownRight
} from "lucide-react";

// Sample portfolio data
// Mock stock data for different timeframes
const mockTimeframeData = {
  // Daily data for 1 week
  "1W": [
    { name: 'Mon', value: 200 },
    { name: 'Tue', value: 205 },
    { name: 'Wed', value: 198 },
    { name: 'Thu', value: 210 },
    { name: 'Fri', value: 215 },
    { name: 'Sat', value: 220 },
    { name: 'Sun', value: 210 },
  ],
  // Weekly data for 1 month
  "1M": [
    { name: 'Week 1', value: 100 },
    { name: 'Week 2', value: 120 },
    { name: 'Week 3', value: 115 },
    { name: 'Week 4', value: 130 },
  ],
  // Monthly data for 3 months
  "3M": [
    { name: 'Jan', value: 100 },
    { name: 'Feb', value: 120 },
    { name: 'Mar', value: 130 },
  ],
  // Monthly data for 1 year
  "1Y": [
    { name: 'Jan', value: 100 },
    { name: 'Feb', value: 120 },
    { name: 'Mar', value: 115 },
    { name: 'Apr', value: 130 },
    { name: 'May', value: 145 },
    { name: 'Jun', value: 160 },
    { name: 'Jul', value: 170 },
    { name: 'Aug', value: 165 },
    { name: 'Sep', value: 180 },
    { name: 'Oct', value: 185 },
    { name: 'Nov', value: 200 },
    { name: 'Dec', value: 210 },
  ],
  // Yearly data for 5 years
  "5Y": [
    { name: '2020', value: 100 },
    { name: '2021', value: 130 },
    { name: '2022', value: 150 },
    { name: '2023', value: 180 },
    { name: '2024', value: 210 },
  ],
};

// Sample portfolio allocation data
const mockPortfolioData = [
  { name: 'Stocks', value: 60 },
  { name: 'Bonds', value: 20 },
  { name: 'Cash', value: 10 },
  { name: 'Real Estate', value: 10 },
];

// Color scheme for the pie chart
const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042'];

export const PortfolioView = () => {
  // State for the selected timeframe (default to 1 month)
  const [timeframe, setTimeframe] = useState("1M");
  // Get the data for the selected timeframe
  const stockData = mockTimeframeData[timeframe as keyof typeof mockTimeframeData];

  return (
    <div className="space-y-6">
      {/* Portfolio Performance and Asset Allocation Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {/* Portfolio Performance Chart */}
        <Card className="md:col-span-2">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg flex justify-between items-center">
              <span>Portfolio Performance</span>
              <select 
                value={timeframe} 
                onChange={(e) => setTimeframe(e.target.value)}
                className="text-sm font-normal border rounded px-2 py-1"
              >
                <option value="1W">1 Week</option>
                <option value="1M">1 Month</option>
                <option value="3M">3 Months</option>
                <option value="1Y">1 Year</option>
                <option value="5Y">5 Years</option>
              </select>
            </CardTitle>
            <CardDescription>Track your portfolio's performance over time</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="h-[300px] w-full">
              <ResponsiveContainer width="100%" height="100%">
                <RechartsLineChart data={stockData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f5f5f5" />
                  <XAxis dataKey="name" stroke="#888888" />
                  <YAxis stroke="#888888" />
                  <RechartsTooltip />
                  <Line
                    type="monotone"
                    dataKey="value"
                    stroke="#8884d8"
                    strokeWidth={2}
                    dot={false}
                    activeDot={{ r: 8 }}
                  />
                </RechartsLineChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        {/* Asset Allocation Pie Chart */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-lg">Asset Allocation</CardTitle>
            <CardDescription>Your current investment mix</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="h-[300px] w-full">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={mockPortfolioData}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    outerRadius={80}
                    fill="#8884d8"
                    dataKey="value"
                    label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                  >
                    {mockPortfolioData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <RechartsTooltip />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Investment Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {/* Total Invested Card */}
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="p-2 bg-green-100 rounded-full">
                <DollarSign className="h-6 w-6 text-green-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Total Invested</p>
                <h3 className="text-2xl font-bold">$12,500.00</h3>
                <p className="text-sm text-green-600 flex items-center gap-1">
                  <ArrowUpRight className="h-4 w-4" /> +12.5% This Month
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        {/* Current Value Card */}
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="p-2 bg-blue-100 rounded-full">
                <TrendingUp className="h-6 w-6 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Current Value</p>
                <h3 className="text-2xl font-bold">$14,250.75</h3>
                <p className="text-sm text-green-600 flex items-center gap-1">
                  <ArrowUpRight className="h-4 w-4" /> +$1,750.75 Growth
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        {/* Annual Return Card */}
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="p-2 bg-purple-100 rounded-full">
                <LineChart className="h-6 w-6 text-purple-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Annual Return</p>
                <h3 className="text-2xl font-bold">14.2%</h3>
                <p className="text-sm text-green-600 flex items-center gap-1">
                  <ArrowUpRight className="h-4 w-4" /> +2.3% from last year
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};
